import { FC } from 'react'
import { Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { DataLoad, StatefulDialog } from 'src/components/ui'
import { useSensorQualityControlRequest } from 'src/hooks/api'
import { DialogNames } from 'src/enums'
import { formatQualityControlSummary } from './qualityControlPresentation'
import { SensorQualityControlTable } from './sensorQualityControlTable'

interface SensorQualityControlDialogProps {
  name: DialogNames
  /** Sensor uniqueIds, IMEIs or devEUIs. The request only runs while the dialog is open. */
  uniqueIds: string[]
  title?: string
  /** When provided the dialog acts as a gate - Cancel / <confirmLabel> - instead of a plain view. */
  confirmLabel?: string
  onConfirm?: () => void
  onClose: () => void
  isLoading?: boolean
}

/**
 * Shows the quality control report for a set of sensors: a summary line and a row per sensor.
 * Used both as a standalone view (the "Quality Control" list button) and as a confirmation step
 * in front of the assign flows.
 */
export const SensorQualityControlDialog: FC<SensorQualityControlDialogProps> = ({
  name,
  uniqueIds,
  title = 'Quality Control',
  confirmLabel,
  onConfirm,
  onClose,
  isLoading = false
}) => {
  const { isInitialLoading, isError, error, data } = useSensorQualityControlRequest({ uniqueIds })
  const rows = data?.rows || []

  return (
    <StatefulDialog
      name={name}
      onClose={onClose}
      wrapContent={false}
      DialogProps={{ maxWidth: 'lg', fullWidth: true }}
      actions={
        <DialogActions>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          {confirmLabel ? (
            <Button variant="contained" onClick={onConfirm} disabled={isLoading || isInitialLoading}>
              {isLoading ? <CircularProgress size={22} /> : confirmLabel}
            </Button>
          ) : null}
        </DialogActions>
      }
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body1">{formatQualityControlSummary(data?.summary)}</Typography>
        </Box>
        <Box height={{ xs: 320, sm: 420 }} minWidth={0}>
          <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
            <SensorQualityControlTable rows={rows} showImei exportedFileName={`${document.title}_quality_control`} />
          </DataLoad>
        </Box>
      </DialogContent>
    </StatefulDialog>
  )
}
